import React, { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { DatePicker, Select, SelectActionList } from "../../../components/form-inputs/inputs";
import { Radio } from "../../../components/form-inputs/inputs/radio";
import { ProductLimits } from "./product-limits";
import { SlaType, turnaroundOptions } from "../lib/consts";
import { ErrorLabel } from "../../../components/form-inputs/shared/error-label";

export const SLA = ({
    selectedClientId,
    customSlaOptions,
    onOpenCreateNewSlaModal,
    onOpenViewClients,
    linkedClients,
    isClientLinkedToSlaGroup,
    onResetSlaFields,
    onUpdateCustomSla,
    onChangeSlaGroup,
}) => {
    const { control } = useFormContext();
    const slaType = useWatch({
        control,
        name: "slaType",
    });
    const slaGroupName = useWatch({
        control,
        name: "slaGroupName",
    });
    const isCustomSLA = slaType === SlaType.CUSTOM;
    const clients = useMemo(() => {
        return customSlaOptions?.map(({ shortName, id }) => {
            return { label: shortName, value: id };
        });
    }, [customSlaOptions]);
    const isLinkedClientSameAsCurrent = useMemo(() => {
        return linkedClients.some(({ clientId }) => clientId === selectedClientId);
    }, [linkedClients, selectedClientId]);

    const shouldShowLinkedClients = () => {
        if (linkedClients.length === 1) {
            return !isLinkedClientSameAsCurrent;
        }

        return linkedClients.length > 0;
    };

    const clientLabel = linkedClients.length === 1 ? "Client" : "Clients";
    const pluralLabel = linkedClients.length === 1 ? "is" : "are";

    return (
        <>
            <h2>SLA</h2>
            <div className="flex">
                {slaType === SlaType.CUSTOM && (
                    <Controller
                        name="startDate"
                        control={control}
                        render={({ field }) => {
                            return (
                                <DatePicker
                                    className="m-r-16 m-b-10 sla-datepicker"
                                    emitAsString
                                    isClearable
                                    label="Start Date"
                                    name={field.name}
                                    value={field.value}
                                    updateOnChange={(name, value) => field.onChange(value)}
                                    enableIcon
                                />
                            );
                        }}
                    />
                )}
            </div>
            <div className="flex-column m-b-16">
                <div className="txt-bold m-b-10">Terms & Conditions</div>
                <div className="flex">
                    <Controller
                        name="slaType"
                        control={control}
                        render={({ field }) => {
                            return (
                                <Radio
                                    style={{ fontSize: "16px" }}
                                    className="m-r-16"
                                    label="Standard (10 days)"
                                    name={field.name}
                                    value={SlaType.STANDARD}
                                    checked={field.value === SlaType.STANDARD}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                        onResetSlaFields();
                                    }}
                                />
                            );
                        }}
                    />
                    <Controller
                        name="slaType"
                        control={control}
                        render={({ field }) => {
                            return (
                                <Radio
                                    style={{ fontSize: "16px" }}
                                    label="Custom SLA"
                                    name={field.name}
                                    value={SlaType.CUSTOM}
                                    checked={field.value === SlaType.CUSTOM}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                        onUpdateCustomSla();
                                    }}
                                />
                            );
                        }}
                    />
                </div>
            </div>
            {isCustomSLA && (
                <Controller
                    name="slaGroupName"
                    control={control}
                    render={({ field, fieldState }) => {
                        return (
                            <div className="align-items-end m-b-10">
                                <div className="sla-client-select m-r-8">
                                    <label
                                        htmlFor={field.name}
                                        className="inline-block m-b-4"
                                    >
                                        SLA Group Name:
                                    </label>
                                    <SelectActionList
                                        {...field}
                                        onChange={(value) => {
                                            field.onChange(value);
                                            onChangeSlaGroup(value);
                                        }}
                                        errorMessage={fieldState?.error?.message}
                                        options={clients}
                                        onClickActionButton={onOpenCreateNewSlaModal}
                                        actionButtonLabel="Create New SLA Group Name"
                                    />
                                </div>
                                {slaGroupName && shouldShowLinkedClients() && (
                                    <div className="m-b-16">
                                        <span>
                                            There {pluralLabel}{" "}
                                            <strong>
                                                {linkedClients.length} {clientLabel}
                                            </strong>{" "}
                                            linked to this SLA Group Name
                                            {isClientLinkedToSlaGroup ? ", including this account." : "."}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => onOpenViewClients(linkedClients)}
                                            className="button -text"
                                        >
                                            View {clientLabel}
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    }}
                />
            )}
            <div className="flex-column m-b-16">
                <Controller
                    name="turnaroundDays"
                    disabled={!isCustomSLA}
                    rules={{
                        required: "Turnaround time is required. Please select a value to continue.",
                    }}
                    control={control}
                    render={({ field, fieldState }) => {
                        return (
                            <>
                                <label
                                    htmlFor={field.name}
                                    className="inline-block txt-bold m-b-4"
                                >
                                    Turnaround Time
                                </label>
                                <ErrorLabel errorMessage={fieldState.error?.message}>
                                    <div className="flex align-center">
                                        <Select
                                            {...field}
                                            className="m-r-8"
                                            excludeSelect={false}
                                            value={(field && field.value) || ""}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                field.onChange(value === "" ? "" : Number(value));
                                            }}
                                            options={turnaroundOptions}
                                        />
                                        <span className="m-b-10">Days</span>
                                    </div>
                                </ErrorLabel>
                            </>
                        );
                    }}
                />
            </div>
            <div className="flex-column m-b-16">
                <div className="txt-bold m-b-10">Limits on Products Submitted</div>
                <ProductLimits isCustomSLA={isCustomSLA} />
            </div>
        </>
    );
};
